// src/rms/components/ReservationEditor.jsx
// ============================================================================
// Editing a booking after it is made
// ============================================================================
// People change their plans constantly, and until now the only answer this
// software had was "cancel it and book it again" — which loses the reservation
// number, the answers to the booking questions, and any deposit already held.
//
// The screen is split along one line, and it is the line that matters:
//
//   DATES re-price. Moving a booking across a weekend, into a busier period or
//   over a length-of-hire tier boundary changes the total, and per-day add-ons
//   change with it. So dates go through previewReschedule -- the operator sees
//   the old total against the new and confirms -- exactly as dragging on the
//   timeline does.
//
//   EVERYTHING ELSE does not. Who it is for, and what is written on it, save
//   directly.
//
// One component, opened from the reservations list and from a click on a
// timeline bar, so both routes get the same guarantees.
// ============================================================================

import React, { useMemo, useState } from "react";
import { useRms } from "../context/RmsContext";
import rentalService from "../data/rentalService";
import { Modal, Field, Select, Banner, formatDateTime } from "./ui";
import adEngine from "../ads/adEngine";

/** A booking that has gone out is history, not a plan. */
const EDITABLE = ["pending", "confirmed"];

function toLocalInput(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const pad = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function ReservationEditor({ reservation, customers = [], onClose, onSaved }) {
  const { operator, t, tLower, formatMoney, isContinuous } = useRms();

  const [customerId, setCustomerId] = useState(reservation.customer_id || "");
  const [notes, setNotes] = useState(reservation.notes || "");
  const [startsAt, setStartsAt] = useState(toLocalInput(reservation.starts_at));
  const [endsAt, setEndsAt] = useState(toLocalInput(reservation.ends_at));

  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState(null);
  const [proposal, setProposal] = useState(null);

  const editable = EDITABLE.includes(reservation.status);

  const datesChanged =
    startsAt !== toLocalInput(reservation.starts_at) ||
    (!isContinuous && endsAt !== toLocalInput(reservation.ends_at));

  const customerOptions = useMemo(
    () => customers.map((c) => ({
      value: c.id,
      label: `${c.first_name} ${c.last_name || ""}`.trim(),
    })),
    [customers]
  );

  /** Everything that does not touch the money. */
  const saveDetails = async () => {
    setBusy(true);
    setProblem(null);
    adEngine.recordAction();
    const result = await rentalService.updateReservation(reservation.id, {
      customer_id: customerId,
      notes,
    });
    setBusy(false);
    if (!result.success) {
      setProblem(result.error);
      return;
    }
    onSaved?.(t("editor.saved", "Booking updated."));
    onClose?.();
  };

  /** Dates go through the engine, and the engine answers with a price. */
  const proposeDates = async () => {
    setBusy(true);
    setProblem(null);
    const result = await rentalService.previewReschedule(
      operator,
      reservation.id,
      new Date(startsAt).toISOString(),
      isContinuous ? null : new Date(endsAt).toISOString()
    );
    setBusy(false);
    if (!result.success) {
      setProblem(result.error);
      return;
    }
    setProposal(result.data);
  };

  const confirmDates = async () => {
    setBusy(true);
    adEngine.recordAction();
    const result = await rentalService.applyReschedule(
      operator,
      reservation.id,
      proposal.after.startsAt,
      proposal.after.endsAt
    );
    setBusy(false);
    if (!result.success) {
      setProblem(result.error);
      return;
    }
    setProposal(null);
    onSaved?.(t("editor.datesSaved", "Dates changed."));
    onClose?.();
  };

  return (
    <Modal
      title={t("editor.title", "{booking} {number}", {
        booking: t("reservation", "Reservation"),
        number: reservation.reservation_number || "",
      })}
      onClose={onClose}
      width={620}
      footer={
        <>
          <button type="button" className="rms-btn rms-btn--ghost" onClick={onClose}>
            {t("action.close", "Close")}
          </button>
          {editable && !proposal ? (
            <button type="button" className="rms-btn rms-btn--primary" onClick={saveDetails} disabled={busy}>
              {t("action.save", "Save")}
            </button>
          ) : null}
        </>
      }
    >
      {problem ? <Banner tone="danger">{problem}</Banner> : null}

      {!editable ? (
        <Banner tone="warn">
          {t(
            "editor.locked",
            "This {booking} is already out or finished, so it can no longer be edited here. Check it in, or cancel it, from the {booking} list.",
            { booking: tLower("reservation", "reservation") }
          )}
        </Banner>
      ) : null}

      <Field label={t("customer", "Customer")}>
        <Select
          value={customerId}
          onChange={setCustomerId}
          options={customerOptions}
          disabled={!editable}
        />
      </Field>

      <Field label={t("editor.notes", "Notes")} help={t("editor.notesHelp", "Anything your staff should know when this goes out.")}>
        <textarea
          className="rms-textarea"
          rows={3}
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          disabled={!editable}
        />
      </Field>

      {/* Dates sit below a rule and behind their own button, because they are
          the half that can change what the customer pays. */}
      <div style={{ borderTop: "1px solid var(--rms-border)", marginTop: 14, paddingTop: 14 }}>
        <h3 className="rms-section-title" style={{ fontSize: 14 }}>
          {t("editor.dates", "Dates")}
        </h3>
        <p style={{ fontSize: 13, color: "var(--rms-muted-strong)", marginTop: 0 }}>
          {t("editor.datesHelp", "Changing these re-prices the booking. You will see the new total before anything is saved.")}
        </p>

        <div className="rms-field-grid">
          <Field label={t("reservations.starts", "Starts")}>
            <input
              className="rms-input"
              type="datetime-local"
              value={startsAt}
              onChange={(e) => setStartsAt(e.target.value)}
              disabled={!editable}
            />
          </Field>
          {!isContinuous ? (
            <Field label={t("reservations.dueBack", "Due back")}>
              <input
                className="rms-input"
                type="datetime-local"
                value={endsAt}
                onChange={(e) => setEndsAt(e.target.value)}
                disabled={!editable}
              />
            </Field>
          ) : null}
        </div>

        {editable && datesChanged && !proposal ? (
          <button type="button" className="rms-btn rms-btn--ghost" onClick={proposeDates} disabled={busy}>
            {t("editor.checkDates", "Check the new price")}
          </button>
        ) : null}

        {proposal ? (
          <>
            {proposal.conflict ? <Banner tone="danger">{proposal.conflict.message}</Banner> : null}
            <div className="rms-tl-compare">
              <div>
                <div className="rms-label">{t("timeline.move.before", "Now")}</div>
                <div>{formatDateTime(proposal.before.startsAt)}</div>
                <div>{proposal.before.endsAt ? formatDateTime(proposal.before.endsAt) : "—"}</div>
                <strong>{formatMoney(proposal.before.totalCents)}</strong>
              </div>
              <div aria-hidden="true" className="rms-tl-compare-arrow">→</div>
              <div>
                <div className="rms-label">{t("timeline.move.after", "After the move")}</div>
                <div>{formatDateTime(proposal.after.startsAt)}</div>
                <div>{proposal.after.endsAt ? formatDateTime(proposal.after.endsAt) : "—"}</div>
                <strong
                  className={
                    proposal.after.totalCents === proposal.before.totalCents
                      ? ""
                      : proposal.after.totalCents > proposal.before.totalCents
                        ? "rms-tl-up"
                        : "rms-tl-down"
                  }
                >
                  {formatMoney(proposal.after.totalCents)}
                </strong>
              </div>
            </div>

            {proposal.after.totalCents !== proposal.before.totalCents ? (
              <Banner tone="warn">
                {t("timeline.move.priceChanged", "The price changes by {delta} on those dates.", {
                  delta: formatMoney(Math.abs(proposal.after.totalCents - proposal.before.totalCents)),
                })}
              </Banner>
            ) : null}

            <div style={{ display: "flex", gap: 10, marginTop: 10 }}>
              <button type="button" className="rms-btn rms-btn--primary" onClick={confirmDates} disabled={busy || proposal.blocked}>
                {t("editor.confirmDates", "Save the new dates")}
              </button>
              <button type="button" className="rms-btn rms-btn--ghost" onClick={() => setProposal(null)} disabled={busy}>
                {t("action.cancel", "Cancel")}
              </button>
            </div>
          </>
        ) : null}
      </div>
    </Modal>
  );
}
